import { buildApiUrl } from "../../config.js";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import PDFUploader from "./PDFUploader";
import { capitalizeWords } from "../ui/capitalizeWords";
import ImageModal from "../Sales Executive/Items Master/ImageModal";
import Select from "react-select";

const style = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  control: (provided) => ({
    ...provided,
    minHeight: "30px",
    height: "30px",
    borderRadius: "6px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "30px",
    padding: "0 4px",
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "30px",
  }),
};

const CreateOrderModal = ({ closeModal, onOrderCreated }) => {
  const [items, setItems] = useState([
    {
      categoryName: "",
      itemName: "",
      itemCode: "",
      itemImage: "",
      qty: 0,
      pcsPerCtn: 0,
      quantitycarton: 0,
    },
  ]);
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const token = localStorage.getItem("token");

  // Scroll Refs
  const formRef = useRef(null);
  const topRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showScrollUp, setShowScrollUp] = useState(false);

  // Scroll Behavior
  useEffect(() => {
    if (!formRef.current || !topRef.current) return;
    const observerDown = new IntersectionObserver(
      ([entry]) => setShowScrollDown(!entry.isIntersecting),
      { threshold: 0 }
    );
    const observerUp = new IntersectionObserver(
      ([entry]) => setShowScrollUp(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-10px" }
    );
    observerDown.observe(formRef.current);
    observerUp.observe(topRef.current);
    return () => {
      observerDown.disconnect();
      observerUp.disconnect();
    };
  }, [formRef, topRef]);

  // Fetch categories and flatten items
  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoryResponse = await axios.get(
          buildApiUrl("/api/categories/all-categories"),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCategories(categoryResponse.data.categories);
        const allItems = categoryResponse.data.categories.flatMap((category) =>
          category.items.map((item) => ({
            ...item,
            categoryName: category.categoryName,
          }))
        );
        setAllItems(allItems);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };
    fetchData();
  }, [token]);

  // Add new item row if the last item is valid
  const handleAddItem = () => {
    const lastItem = items[items.length - 1];
    if (lastItem.itemCode.trim() !== "" && lastItem.qty !== 0) {
      setItems([
        ...items,
        {
          categoryName: "",
          itemName: "",
          itemCode: "",
          itemImage: "",
          qty: 0,
          pcsPerCtn: 0,
          quantitycarton: 0,
        },
      ]);
    } else {
      toast.error(
        "Please select an item and enter a quantity greater than 0 to add a new row."
      );
    }
  };

  // Handle changes in item selection
  const handleItemChange = (index, selectedOption) => {
    const selectedItemCode = selectedOption ? selectedOption.value : "";
    const selectedItem = allItems.find(
      (item) => item.itemCode === selectedItemCode
    );
    if (selectedItem) {
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        categoryName: selectedItem.categoryName,
        itemName: selectedItem.itemName,
        itemCode: selectedItem.itemCode,
        itemImage: selectedItem.itemImage,
        pcsPerCtn: selectedItem.pcsPerCtn,
      };
      setItems(newItems);
    }
  };

  // Remove an item row
  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      toast.error("At least one item is required.");
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // Handle quantity changes
  const handleInputChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === "qty") {
      const pcsPerCtn = newItems[index].pcsPerCtn || 1;
      newItems[index]["quantitycarton"] = Math.floor(value / pcsPerCtn);
    } else if (field === "quantitycarton") {
      const pcsPerCtn = newItems[index].pcsPerCtn || 1;
      newItems[index]["qty"] = value * pcsPerCtn;
    }
    setItems(newItems);
  };

  // Submit order
  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasZeroQuantity = items.some((item) => item.qty === 0);
    if (hasZeroQuantity) {
      toast.error(
        "Cannot create order: One or more items have a quantity of zero."
      );
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        buildApiUrl("/api/orderItem/create"),
        { items },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Order created successfully!");
      onOrderCreated(response.data);
      closeModal();
    } catch (error) {
      toast.error("Error creating order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Image modal handlers
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage("");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-md shadow-lg p-4 w-11/12 max-w-7xl overflow-y-auto max-h-[90vh]">
        <div ref={topRef}>
          <h2 className="text-2xl font-semibold mb-4">Create New Order</h2>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Items Section */}
          <div className="hidden sm:grid mb-2 grid-cols-[repeat(7,1fr)_20px] gap-2 bg-gray-300 py-1 rounded-md font-semibold text-xs">
            <span className="pl-2">Item</span>
            <span>Category</span>
            <span>Item Code</span>
            <span>Pcs/Ctn</span>
            <span>Image</span>
            <span>Quantity</span>
            <span>Qty/Ctn</span>
            <span></span>
          </div>
          {items.map((item, index) => (
            <fieldset
              key={index}
              className="border sm:border-0 border-gray-500 p-4 sm:p-0 mb-4 sm:mb-2 rounded-md"
            >
              <legend className="px-2 font-semibold block sm:hidden">
                Item {index + 1}
              </legend>

              <div className="grid grid-cols-1 md:grid-cols-[repeat(7,1fr)_20px] gap-2">
                <div className="w-full text-sm">
                  <label className="sm:hidden">Item</label>
                  <Select
                    name="itemCode"
                    value={
                      allItems.find(
                        (option) => option.itemCode === item.itemCode
                      )
                        ? {
                            value: item.itemCode,
                            label: capitalizeWords(item.itemName),
                          }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleItemChange(index, selectedOption)
                    }
                    options={allItems.map((itemOption) => ({
                      value: itemOption.itemCode,
                      label: capitalizeWords(itemOption.itemName),
                    }))}
                    styles={style}
                    menuPortalTarget={document.body}
                    placeholder="Select Item"
                    className="w-full"
                  />
                </div>
                <div className="w-full text-sm">
                  <label className="sm:hidden">Category</label>
                  <input
                    type="text"
                    value={capitalizeWords(item.categoryName)}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md bg-gray-100"
                    disabled
                  />
                </div>
                <div className="w-full text-sm">
                  <label className="sm:hidden">Item Code</label>
                  <input
                    type="text"
                    value={item.itemCode}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md bg-gray-100"
                    disabled
                  />
                </div>
                <div className="w-full text-sm">
                  <label className="sm:hidden">Pcs/Ctn</label>
                  <input
                    type="text"
                    value={item.pcsPerCtn}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md bg-gray-100"
                    disabled
                  />
                </div>
                <div className="w-full text-sm">
                  <label className="sm:hidden">Image</label>
                  {item.itemImage ? (
                    item.itemImage.includes("drive.google.com") ? (
                      <img
                        src={`https://drive.google.com/thumbnail?id=${
                          item.itemImage.split("id=")[1]
                        }`}
                        alt={item.itemName}
                        className="size-4 rounded-md cursor-pointer"
                        onClick={() => handleImageClick(item.itemImage)}
                      />
                    ) : (
                      <img
                        src={item.itemImage}
                        alt={item.itemName}
                        className="size-4 rounded-md cursor-pointer"
                        onClick={() => handleImageClick(item.itemImage)}
                      />
                    )
                  ) : (
                    <p className="text-xs text-gray-500">No Image</p>
                  )}
                </div>
                <div className="w-full text-sm">
                  <label className="sm:hidden">Quantity</label>
                  <input
                    required
                    type="number"
                    value={item.qty}
                    onChange={(e) =>
                      handleInputChange(index, "qty", e.target.value)
                    }
                    className="w-full px-2 py-1 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="w-full text-sm">
                  <label className="sm:hidden">Qty/Ctn</label>
                  <input
                    required
                    type="number"
                    value={item.quantitycarton}
                    onChange={(e) =>
                      handleInputChange(index, "quantitycarton", e.target.value)
                    }
                    className="w-full px-2 py-1 border border-gray-300 rounded-md"
                  />
                </div>
                {/* {index === items.length - 1 && (
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="sm:text-blue-500 flex sm:hidden items-center gap-2 sm:hover:text-blue-700 cursor-pointer text-white mr-2"
                    >
                      <FiPlus size={16} />
                      <span className="block sm:hidden">Add</span>
                    </button>
                  )} */}
                {items.length > 1 && (
                  <div className="flex bg-red-600 py-1 sm:py-0 rounded-md sm:bg-white items-center justify-center">
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      className="sm:text-red-500 flex items-center gap-2 sm:hover:text-red-700 cursor-pointer text-white"
                    >
                      <FiTrash2 size={16} />
                      <span className="block sm:hidden">Delete</span>
                    </button>
                  </div>
                )}
              </div>
            </fieldset>
          ))}

          {/* Buttons */}
          <div className="flex gap-2 justify-end mt-4">
            <button
              type="button"
              onClick={handleAddItem}
              className="text-blue-500 flex items-center border border-blue-600 px-3 text-xs gap-2 hover:text-blue-700 cursor-pointer  rounded-md"
            >
              <FiPlus size={16} />
              <span className="">Add Item</span>
            </button>
            <button
              type="button"
              onClick={closeModal}
              className="px-3 text-xs py-2 cursor-pointer bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              ref={formRef}
              disabled={loading}
              className={`px-3 text-xs py-2 cursor-pointer bg-red-600 text-white rounded-md hover:bg-red-700 ${
                loading ? "opacity-75 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Creating..." : "Save"}
            </button>
          </div>
        </form>

        {/* Scroll Buttons */}
        <div className="hidden sm:block">
          {showScrollDown && (
            <button
              onClick={() =>
                formRef.current?.scrollIntoView({ behavior: "smooth" })
              }
              className="fixed bottom-4 right-4 z-50 cursor-pointer bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md shadow-lg"
            >
              <FaArrowDown />
            </button>
          )}
          {showScrollUp && (
            <button
              onClick={() =>
                topRef.current?.scrollIntoView({ behavior: "smooth" })
              }
              className="fixed bottom-4 right-16 z-50 cursor-pointer bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md shadow-lg"
            >
              <FaArrowUp />
            </button>
          )}
        </div>

        {isImageModalOpen && (
          <ImageModal imageUrl={selectedImage} onClose={closeImageModal} />
        )}
        <ToastContainer />
      </div>
    </div>
  );
};

export default CreateOrderModal;
